import apiClient from './axios';
import { ApiResponse } from '../types';

/**
 * Where PayIQ's ledger and the bank disagree about a company account.
 *
 * Read-only apart from the re-run — there is deliberately no endpoint that
 * adjusts a ledger to match the provider, because a correction like that
 * papers over whatever caused the gap and is indistinguishable from the
 * fault afterwards.
 */

export type ReconciliationSeverity =
  | 'clean'
  | 'expected'
  | 'drift'
  | 'unreachable';

export interface ReconciliationSummary {
  id: string;
  accountId: string;
  businessId: string;
  accountNumber: string;
  severity: ReconciliationSeverity;
  /** The exact figure, so nothing here has to trust a float. */
  driftKobo: string;
  driftNaira: number;
  ledgerSettledNaira: number;
  ledgerInFlightNaira: number;
  /** Null when the provider couldn't be read — not zero, which would read
   *  as "the bank says the account is empty". */
  providerBalanceNaira: number | null;
  explanation: string;
  checkedAt: string;
  /**
   * Set once somebody worked this drift out.
   *
   * Not a correction — the drift above keeps its figure. This records that a
   * named human accounted for it, so an explained discrepancy stops arriving
   * every morning looking exactly like a new one.
   */
  acknowledgedAt: string | null;
  acknowledgedBy: string | null;
  acknowledgementNote: string | null;
}

/** One row of the ledger the drift disagrees with. */
export interface ReconciliationActivity {
  id: string;
  type: 'credit' | 'debit';
  status: string;
  category: string;
  amount: number;
  fee: number;
  counterparty: string | null;
  reference: string;
  narration: string | null;
  createdAt: string;
}

export interface StuckTransfer {
  id: string;
  businessId: string;
  reference: string;
  amount: number;
  accountNumber: string;
  bankCode: string;
  createdAt: string;
  updatedAt: string;
}

export interface SweepResult {
  checked: number;
  drifting: number;
  unreachable: number;
  runBy: string;
}

export const businessReconciliationApi = {
  list: async (params: { severity?: ReconciliationSeverity } = {}) => {
    const { data } = await apiClient.get<ApiResponse<ReconciliationSummary[]>>(
      '/admin/business-reconciliation',
      { params },
    );
    return data.data;
  },

  history: async (accountId: string) => {
    const { data } = await apiClient.get<ApiResponse<ReconciliationSummary[]>>(
      `/admin/business-reconciliation/accounts/${accountId}`,
    );
    return data.data;
  },

  stuck: async () => {
    const { data } = await apiClient.get<ApiResponse<StuckTransfer[]>>(
      '/admin/business-reconciliation/stuck',
    );
    return data.data;
  },

  /** What the drift is made of: the account's recent ledger, newest first. */
  activity: async (accountId: string) => {
    const { data } = await apiClient.get<ApiResponse<ReconciliationActivity[]>>(
      `/admin/business-reconciliation/accounts/${accountId}/activity`,
    );
    return data.data;
  },

  /** Re-check one account. The question after a fix is "is *this* still
   *  wrong?", and sweeping the estate to answer it costs a provider call per
   *  business and buries the answer in a count. */
  recheck: async (accountId: string) => {
    const { data } = await apiClient.post<ApiResponse<{ severity: string; driftNaira: number; explanation: string }>>(
      `/admin/business-reconciliation/accounts/${accountId}/recheck`,
    );
    return data.data;
  },

  /** Record that a human worked this one out. The note is required — see the
   *  backend; "acknowledged" with no reason reads as "dismissed" later. */
  acknowledge: async (id: string, note: string) => {
    const { data } = await apiClient.post<ApiResponse<ReconciliationSummary>>(
      `/admin/business-reconciliation/${id}/acknowledge`,
      { note },
    );
    return data.data;
  },

  run: async () => {
    const { data } = await apiClient.post<ApiResponse<SweepResult>>(
      '/admin/business-reconciliation/run',
    );
    return data.data;
  },
};
